import { call } from '@advanza/api'
import { Button, Divider } from '@advanza/ui'
import { DIALOG_BACKDROP_FADE } from '@trustoo/components/modal/constants/dialogPresets'
import Dialog from '@trustoo/components/modal/Dialog'
import AutoRow from 'components/misc/AutoRow'
import { useAppDispatch } from 'hooks/hooks'
import { useState } from 'react'

interface Props {
    providerId: number
}

type BadDebtSuccessResponse = {
    status: 'success'
    totalOpenInvoices: number
    openInvoiceNrs: string[]
    ban: object
    note: string
}

type CallError =
    | {
          status: 'error'
          error: string
      } // when json.status !== 'success'
    | Error // network errors

const PerformBadDebtProcedureButton = ({ providerId }: Props) => {
    const [isLoading, setIsLoading] = useState(false)
    const [response, setResponse] = useState<BadDebtSuccessResponse | null>(null)
    const [error, setError] = useState<CallError | null>(null)
    const [open, setOpen] = useState(false)
    const dispatch = useAppDispatch()

    const performBadDebtProcedure = async () => {
        setIsLoading(true)
        setError(null) // Clear previous error
        setResponse(null) // Clear previous response

        try {
            const apiResponse = await call(`office/invoices/perform-bad-debt-procedure`, {
                json: {
                    providerId,
                },
            })
            setResponse(apiResponse)
            dispatch({ type: 'INVALIDATE_INVOICES' })
        } catch (e: unknown) {
            setError(e as CallError)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <Button appearance="secondary" iconBefore="heart_broken" onClick={() => setOpen(true)}>
                Perform bad debt under 100€ procedure
            </Button>
            <Dialog.Root
                open={open}
                onClose={() => setOpen(false)}
                className={`md:w-[500px] md:max-w-full ${DIALOG_BACKDROP_FADE}`}>
                <Dialog.CloseButton onClick={() => setOpen(false)} aria-label="Close" />
                <div className="p-4">
                    <h3>Are you sure?</h3>
                    <p>
                        This will perform the following actions if the total of open invoices is
                        under 100€:
                    </p>
                    <ul>
                        <li>Unsubscribe the pro</li>
                        <li>Quarantine the provider</li>
                        <li>Create an internal note: "Lost"</li>
                        <li>Set status of all open invoices to "Lost"</li>
                    </ul>
                    <Divider m />
                    <AutoRow>
                        <Button
                            disabled={isLoading}
                            appearance="primary"
                            onClick={performBadDebtProcedure}>
                            {isLoading ? 'Processing...' : 'Confirm bad debt procedure'}
                        </Button>
                        <Button appearance="secondary" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                    </AutoRow>
                    {(response || error) && (
                        <pre>{JSON.stringify({ response, error }, null, 2)}</pre>
                    )}
                </div>
            </Dialog.Root>
        </div>
    )
}

export default PerformBadDebtProcedureButton
